import { StateData } from "../fstn/state";
import { RegisterData, RegisterValue } from "../fstn/register";
import { MarshalValueData } from "../fstn/marshalValue";
import { MarshalData } from "../fstn/marshal";
import { TransitionData } from "../fstn/transition";
import {shim} from "string.prototype.matchall";

shim();

export class ErrorNonNumericRegister extends Error
{
    constructor(name: string, register: (RegisterValue | undefined))
    {
        super(`Expected a numeric register named ${name} but received ${register}`)
    }
}
export class ImportError extends Error
{
    node: object;

    constructor(reason: string, node: any)
    {
        super(reason);
        this.node = node;
        Error.captureStackTrace(this, ImportError);
    }
}
export interface IMetaData
{
    parent: string
    script?: number
    summary?: number
    name?: number
    index?: number
}

const conditionalPattern = /^CONDITIONAL\((?<trigger>[^,]+),\s*(?<condition>.+)\)/mg;
const gesturePattern = /\*\s*Gesture choice:\s*(.*)/mg;
const optionPattern = /^\s*\*\s*(.*)/mg;
const containsPattern = /^Contains:/;
const doNotLocalizeFlag = 2;

type TextData = {
    m_contents?: string
}
export type PathNodeData = {
    m_badges: Array<string>
    m_components: Array<string>
    m_editableText0?: TextData
    m_ulidTransitionEnd: string
    m_pos?: { x: number, y: number }
    m_type: string
    m_ulid: string
    m_ulidRoot: string
    m_ulidParent: string
};
export type TouchVideoPathData = {
    m_name?: TextData
    nodes?: Array<PathNodeData>
    blocks?: Array<{ m_blockName: string }>
    entities?: EntityData[]
};
interface EntityData
{
    m_name?: TextData
    m_entity: {
        m_componentFlagsExt: number,
        m_components: string[],
        m_ulid: string
    },
    m_clipDesc?: {
        m_filepath: string,
        m_loopMode: ("Normal" | "Looped")
    },
    m_text: {
        text: string
    }
}
interface IVariableData
{
    name: string
    type: string
    initialValue: string | number
}
type Registers = { [register: string]: RegisterData };

export type ImportedSource = { [root: string]: TouchVideoPathData };
export interface TouchVideoLeafData
{
}
export interface IImportedData
{
    localized: { [key: string]: string }
    meta: { [key: string]: IMetaData }
    states: { [key: string]: StateData }
    initial: string[]
}

export function parseTouchVideoFlow(source: ImportedSource, onProgress?: (progress: string) => void): IImportedData
{
    const triggers: { [key: string]: PathNodeData[] } = {};
    const gameVarSwitches: string[] = [];
    const gameVarSets: { [key: string]: string } = {};
    const meta: { [key: string]: IMetaData } = {};
    const states: { [key: string]: StateData } = {};
    const nonStates: { root: string, index: number }[] = [];
    const initial: string[] = [];
    const hidden: string[] = [];
    const nodes: { [key: string]: PathNodeData } = {};
    const addState = (key: string, parent: string, index: number) =>
    {
        meta[key] = { parent, index };
        states[key] = {
            actions: [],
            name: "",
            parent,
            script: "",
            transitions: [
            ]
        };
        return states[key];
    };
    const blockNames: {[state: string]: string[]} = {};
    const localized: {[id: string]: string} = {};

    Object.keys(source || {}).forEach(root =>
    {
        if (source[root].nodes) {
            source[root].nodes?.forEach((node, index) =>
            {
                if (['Composite', 'Game Var Switch', 'Game Var Set', 'Hub', 'Leaf'].includes(node.m_type))
                {
                    const state = addState(node.m_ulid, node.m_ulidRoot, index);

                    if (node.m_badges?.includes("Start Node"))
                    {
                        initial.push(node.m_ulid);
                    }
                    if (node.m_badges?.includes("Hidden From Game"))
                    {
                        hidden.push(node.m_ulid);
                    }
                    if (node.m_type === "Game Var Switch")
                    {
                        gameVarSwitches.push(node.m_ulid);
                    } else if (node.m_type === "Game Var Set")
                    {
                        gameVarSets[node.m_ulid] = node.m_editableText0?.m_contents ?? "";
                    }
                    states[node.m_ulid] = state;
                    state.meta = state.meta ?? { type: "default" };
                    state.meta.position = { x: node.m_pos?.x ?? 0, y: node.m_pos?.y ?? 0 };
                } else if (node.m_type === 'Jump')
                {
                    const state = addState(node.m_ulid, node.m_ulidRoot, index)

                    state.meta = {
                        type: "jump",
                        position: { x: node.m_pos?.x ?? 0, y: node.m_pos?.y ?? 0 }
                    };
                    state.transitions.push({
                        destination: node.m_ulidTransitionEnd,
                        meta: { type: "jump" }
                    });
                } else if (node.m_type === "Trigger" && node.m_editableText0?.m_contents !== "null")
                {
                    if (triggers[node.m_ulidRoot] === undefined)
                    {
                        triggers[node.m_ulidRoot] = [];
                    }
                    triggers[node.m_ulidRoot].push(node);
                } else
                {
                    nonStates.push({ root, index });
                }
                nodes[node.m_ulid] = node;
            });
        } else {
            // Leaves
            if (source[root].entities) {
                const blocks = source[root].entities!
                    .filter(entity => (entity.m_entity.m_components.includes("Block") ||
                        entity.m_entity.m_components.includes("Clip")) && entity.m_text?.text)
                    .map(block => block.m_text!.text as string);
                blockNames[root] = [...blockNames[root] || [], ...blocks];
                source[root].entities!.forEach(entity => {
                    if (entity.m_text?.text && !(Number(entity.m_entity.m_componentFlagsExt) & doNotLocalizeFlag)) {
                        localized[entity.m_entity.m_ulid] = entity.m_text.text;
                    }
                });
            }
        }
    });
    Object.keys(blockNames).forEach(state => {
        if (states[state]) {
            if (states[state].meta === undefined) {
                states[state].meta = {
                    type: "default",
                    blockNames: blockNames[state]
                };
            } else if (states[state].meta?.blockNames === undefined) {
                states[state].meta!.blockNames = blockNames[state];
            } else {
                states[state].meta!.blockNames!.push(
                    ...blockNames[state].filter(blockName => ! states[state].meta!.blockNames!.includes(blockName)));
            }
        }
    });
    onProgress && onProgress(`Parsing ${Object.keys(nodes).length} nodes`);
    nonStates.forEach(nonState =>
    {
        const node = source[nonState.root].nodes![nonState.index];
        const parent = states[node.m_ulidParent];
        const metaData = meta[node.m_ulidParent];

        if (parent)
        {
            const end = nodes[node.m_ulidTransitionEnd];
            if (node.m_components.length === 3 && node.m_components[2] === "Summary Text")
            {
                metaData.summary = nonState.index;
                parent.summary = node.m_editableText0?.m_contents;
            } else if (node.m_components.length === 3 && node.m_components[2] === "Title Text")
            {
                metaData.name = nonState.index;
                parent.name = node.m_editableText0?.m_contents ?? "";
            } else if (node.m_components.length === 3 && node.m_components[2] === "Script Text")
            {
                metaData.script = nonState.index;
                parent.script = node.m_editableText0?.m_contents ?? "";
            } else if (node.m_components.length === 3 && node.m_components[2] === "Start Connector" &&
                node.m_ulidTransitionEnd !== "00000000000000000000000000")
            {
                const connector = nodes[node.m_ulidTransitionEnd];

                if (connector)
                {
                    if (connector.m_components[2] === "End Connector")
                    {
                        // Connecting to another state in this one
                        parent.transitions.push({
                            destination: connector.m_ulidParent,
                            trigger: node.m_editableText0?.m_contents
                        });
                    } else if (connector.m_components[2] === "Start Connector")
                    {
                        // Connecting to the start connector of another state so follow it
                        const nextConnector = nodes[connector.m_ulidTransitionEnd];

                        parent.transitions.push({
                            destination: nextConnector?.m_ulidParent,
                            trigger: node.m_editableText0?.m_contents,
                            meta: {
                                type: "jumpState"
                            }
                        });
                    }
                }
            } else if (end)
            {
                if (node.m_components.length === 3 && node.m_components[2] === "End Connector" &&
                    end.m_ulidRoot !== node.m_ulidRoot)
                {

                    // This transition is to a state in a container so mark that as the child of this state
                    parent.child = end.m_ulidParent;
                } else if (node.m_components.length === 3 && node.m_components[2] === "Transition Text")
                {
                    const transition = parent.transitions.find(transition =>
                        transition.destination === end.m_ulidParent);

                    if (transition && node.m_editableText0?.m_contents !== "")
                    {
                        transition.trigger = node.m_editableText0?.m_contents;
                    }
                }
            }
        }
    });
    onProgress && onProgress(`Parsing ${Object.keys(states).length} nodes`);

    const matchPattern = (test: string, pattern: RegExp, callback: (match: RegExpMatchArray) => any): boolean =>
    {
        const match = test?.match(pattern);

        if (match)
        {
            callback(match);
            return true;
        }
        return false;
    };

    const makeMarshalValueData = (value: string): MarshalValueData =>
    {
        const numberValue = Number.parseInt(value);

        if (!Number.isNaN(numberValue))
        {
            return { value: numberValue };
        } else if (value === "true" || value === "false")
        {
            return { value: value === "true" };
        } else
        {
            return { reference: [value] };
        }
    };
    const parseUnary = (test: string, pattern: RegExp,
        callback: (value: MarshalValueData) => any): boolean =>
    {
        return matchPattern(test, pattern, (match) =>
        {
            callback(makeMarshalValueData(match[1]));
        })
    };
    const parseBinary = (test: string, pattern: RegExp,
        callback: (key: MarshalValueData, value: MarshalValueData) => any): boolean =>
    {
        return matchPattern(test, pattern, (match) =>
        {
            callback(makeMarshalValueData(match[1]), makeMarshalValueData(match[2]));
        })
    };
    const parseTrinary = (test: string, pattern: RegExp, callback: (key: MarshalValueData, value: MarshalValueData,
        increment: MarshalValueData) => any): boolean =>
    {
        return matchPattern(test, pattern, (match) =>
        {
            callback(makeMarshalValueData(match[1]), makeMarshalValueData(match[2]), makeMarshalValueData(match[3]));
        })
    };
    const parseNodeFromScript = (script: string, key: string, actions: MarshalData[], transitions: TransitionData[]) =>
    {
        
        if (script.length && !script.match(containsPattern))
        {
            const stateTriggers = [];

            script.match(gesturePattern)?.forEach((match, index) =>
            {
                const trigger = match.replace("* Gesture choice: ", "");

                if (transitions.length <= index)
                {
                    transitions[index] = {};
                }
                const destination = states[transitions[index].destination || ""];

                stateTriggers.push(trigger);
                transitions[index].trigger = trigger;
                actions?.push({
                    action: "AddTouchGesture",
                    parameters: {
                        type: { value: "button" },
                        hint: { value: trigger },
                        trigger: { value: trigger },
                        disabled: { value: destination === undefined }
                    }
                });
                script = script.replace(match, "");
            });
            script.match(optionPattern)?.forEach((match, index) =>
            {
                if (transitions.length <= stateTriggers.length)
                {
                    transitions[stateTriggers.length] = {};
                }

                const destination = states[transitions[stateTriggers.length].destination || ""];
                const trigger = match.replace(/^\s*\*\s*/, "");
                const nodeTriggers = triggers[key] || [];
                const nodeData = nodeTriggers.length > index ?
                    JSON.parse(nodeTriggers[index].m_editableText0?.m_contents ?? `{"conditions": []}`) : { conditions: [] };
                const parameters = {
                    option: { value: trigger },
                    trigger: { value: trigger },
                    remote: { value: `${index + 1}` },
                    disabled: { value: destination === undefined },
                    visible: { value: true }
                };

                transitions[stateTriggers.length].trigger = trigger;
                stateTriggers.push(trigger);
                if (nodeData.conditions?.some((condition: any) => condition.m_name === "selectedRegion"))
                {
                    // Hide options which are based on clicking on the screen
                    parameters.visible.value = false;
                }
                actions?.push({
                    action: "AddOption",
                    parameters
                });
                script = script.replace(match, "");
            });
            // Pull any triggers out of conditionals
            for (let conditional of script.matchAll(conditionalPattern))
            {
                if (conditional.groups)
                {
                    const { trigger } = conditional.groups;

                    if (transitions.length > stateTriggers.length)
                    {
                        transitions[stateTriggers.length].trigger = trigger;
                        stateTriggers.push(trigger);
                    }
                }
            }
            if (stateTriggers.length === 0 && transitions.length === 1)
            {
                const destination = states[transitions[0].destination || ""];
                const nodeTriggers = triggers[key] || [];
                let nodeData;

                if (nodeTriggers.length > 0)
                {
                    try
                    {
                        nodeData = JSON.parse(nodeTriggers[0].m_editableText0?.m_contents ?? "");
                    } catch (exception)
                    {
                        console.error(`Failed to parse node trigger ${nodeTriggers[0].m_editableText0?.m_contents}`)
                        nodeData = { conditions: [] };
                    }
                } else
                {
                    nodeData = { conditions: [] };
                }
                transitions[0].trigger = "continue";
                if (nodeData.conditions.some((condition: any) => condition.m_name === "clipEnd"))
                {
                    // Adding a video will add the video end event so nothing here
                } else if (script && !hidden.includes(key))
                {
                    actions.push({
                        action: "AddTouchGesture",
                        parameters: {
                            disabled: { value: destination === undefined },
                            type: { value: "touch" },
                            hint: { value: "continue" },
                            trigger: { value: "continue" }
                        }
                    });
                } else
                {
                    actions.push({
                        action: "Trigger",
                        parameters: {
                            trigger: { value: "continue" }
                        }
                    });
                }
            }
        }
    };
    const unaryTestOperators: { [key: string]: RegExp } = {
        boolean: /^([a-zA-Z_]+)$/,
        not: /^!\s*(\S+)$/,
    };
    const binaryTestOperators: { [key: string]: RegExp } = {
        and: /^(\S+)\s+and\s+(\S+)$/,
        or: /^(\S+)\s+or\s+(\S+)$/,
        equals: /^(\S+)\s+==\s+(\S+)$/,
        greater: /^(\S+)\s+>\s+(\S+)$/,
        greaterEqual: /^(\S+)\s+>=\s+(\S+)$/,
        less: /^(\S+)\s+<\s+(\S+)$/,
        lessEqual: /^(\S+)\s+<=\s+(\S+)$/,
        notEquals: /^(\S+)\s+~=\s+(\S+)$/,
    };

    Object.keys(states).forEach((key) =>
    {
        const state = states[key];

        state.actions = [];
        if (state.script)
        {
            parseNodeFromScript(state.script, key, state.actions, state.transitions);
        } else
        {
            if (state.transitions.length === 1)
            {
                state.transitions[0].trigger = "continue";
                state.actions.push({
                    action: "Trigger",
                    parameters: { trigger: { value: "continue" } }
                });
            } else if (state.transitions.length > 1)
            {
                state.transitions.forEach((transition, index) =>
                {
                    const trigger = transition.trigger || `option ${index + 1}`;

                    state.actions?.push({
                        action: "AddOption",
                        parameters: {
                            option: { value: trigger },
                            trigger: { value: trigger },
                            remote: { value: `${index + 1}` },
                            disabled: { value: transition.destination === undefined }
                        }
                    });
                });
            }
        }
        if (gameVarSwitches.includes(key))
        {
            state.actions?.unshift({
                action: "ClearRenderer",
                parameters: {}
            });
            state.transitions.forEach(({ trigger }) =>
            {
                if (trigger)
                {
                    if (!Object.keys(binaryTestOperators).some(operator =>
                        parseBinary(trigger, binaryTestOperators[operator], (lhs, rhs) =>
                        {
                            state.actions?.push({
                                action: "Test",
                                parameters: { lhs, rhs, operator: { value: operator }, trigger: { value: trigger } }
                            });
                        })))
                    {
                        // Failed to parse binary, try unary
                        Object.keys(unaryTestOperators).some(operator =>
                            parseUnary(trigger, unaryTestOperators[operator], (value) =>
                                state.actions.push({
                                    action: "Test",
                                    parameters: { value, operator: { value: operator }, trigger: { value: trigger } }
                                })));
                    }
                }
            });
        } else if (gameVarSets[key])
        {
            parseBinary(gameVarSets[key], /^(\S+)\s+=\s+(\S+)$/, (key, value) =>
            {
                state.actions?.unshift({
                    action: "SetRegister",
                    parameters: { key, value }
                });
            }) ||
                parseTrinary(gameVarSets[key], /^(\S+)\s+=\s+(\S+)\s*\+\s*(\S+)$/,
                    (key, value, increment) =>
                    {
                        state.actions?.unshift({
                            action: "IncrementRegister",
                            parameters: { key, value, increment }
                        });
                    }) ||
                parseTrinary(gameVarSets[key], /^(\S+)\s+=\s+(\S+)\s*-\s*(\S+)$/,
                    (key, value, increment) =>
                    {
                        if (typeof increment.value !== "number")
                        {
                            throw new ErrorNonNumericRegister("increment", increment.value);
                        }
                        increment.value *= -1;
                        state.actions?.unshift({
                            action: "IncrementRegister",
                            parameters: { key, value, increment }
                        });
                    })
        }
        if (state.transitions.length > 1)
        {
            state.transitions.forEach((transition, index) =>
            {
                if (!transition.trigger)
                {
                    if (gameVarSwitches.includes(key))
                    {
                        transition.trigger = "else";
                        state.actions.push({ action: "Trigger", parameters: { trigger: { value: transition.trigger } } });
                    }
                    else
                    {
                        transition.trigger = `option ${index + 1}`;
                    }
                }
            });
            state.transitions.sort((a, b) =>
            {
                return b.trigger === "else" && a.trigger !== "else" ? -1 : 0;
            })
        }
    });
    if (initial.length === 0) {
        const first = Object.keys(states)[0];

        console.error(`No initial state set, using first state found: ${first}`);
        initial.push(first);
    }

    const initialState = initial[initial.length - 1];
    const recurseDepth = (state: string, visited: string[], initialDepth: number) =>
    {
        const stateData = states[state];

        if (stateData) {
            stateData.meta = stateData.meta ?? {type: "default"};
            stateData.meta.depth = initialDepth;

            stateData.transitions.forEach((transition) => {
                let depth = initialDepth;
                let state = transition.destination;
                while (state !== undefined && !visited.includes(state)) {
                    visited.push(state);
                    const stateData = states[state];

                    if (stateData) {
                        stateData.meta = stateData.meta ?? {type: "default"};
                        stateData.meta.depth = ++depth;

                        if (stateData.child) {
                            state = stateData.child;
                        } else if (stateData.transitions.length === 0) {
                            break;
                        } else if (stateData.transitions.length > 1) {
                            recurseDepth(state, visited, depth);
                        } else {
                            state = stateData.transitions[0].destination ?? "";
                        }
                    }
                }
            });
        }
    }

    recurseDepth(initialState, [], 0);

    return { localized, meta, states, initial };
}
export function importVariablesFromLockingServer(project: string): Promise<Registers>
{
    return new Promise((resolve, reject) =>
    {
        const webSocket = new WebSocket("ws://touchvideo.tv/tvserver");
        let sessionID = "";

        webSocket.onmessage = (event) =>
        {
            const data = JSON.parse(event.data);

            console.log(data);
            if (data.message === "OPEN_SESSION_SUCCESS")
            {
                sessionID = data.sessionID;
                webSocket.send(JSON.stringify({
                    message: "OPEN_PROJECT",
                    repoBranch: "design/develop",
                    flowName: project,
                    sessionID
                }));
            } else if (data.message === "OPEN_PROJECT_SUCCESS")
            {
                const registers: Registers = {};

                (data.variables as IVariableData[]).forEach(variable =>
                {
                    if (variable.type === "String")
                    {
                        registers[variable.name] = {
                            type: "string",
                            value: variable.initialValue as string
                        };
                    } else if (variable.type === "Number")
                    {
                        registers[variable.name] = {
                            type: "number",
                            value: variable.initialValue as number
                        };
                    } else if (variable.type === "Boolean")
                    {
                        registers[variable.name] = {
                            type: "boolean",
                            value: typeof variable.initialValue === "string" ? variable.initialValue === "true" : Boolean(variable.initialValue)
                        };
                    }
                });
                resolve(registers);
                webSocket.close();
            } else
            {
                reject(data);
                webSocket.close();
            }
        };
        webSocket.onopen = () =>
        {
            webSocket.send(JSON.stringify({ message: "OPEN_SESSION" }));
        };
    });
}